import type { WorkingHours } from "@ai-admin/shared";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc, trpcErrorCode } from "../../app/trpc.js";
import { Button, CloseIcon, focusRing, IconButton, Input, Notice, Toggle } from "../../ui/index.js";

/** Неделя с понедельника; в базе 0 это воскресенье. */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const;
const hh = (s: string) => s.slice(0, 5);
/** Столько интервалов за неделю принимает сервер (`calendar.workingHours.set`). */
const MAX_INTERVALS = 21;
/** Пауза после последней правки перед автосохранением. */
export const AUTOSAVE_DELAY_MS = 800;

export const DEFAULT_HOURS: WorkingHours[] = [1, 2, 3, 4, 5, 6].map((weekday) => ({ weekday, start: "09:00", end: "18:00" }));

/** Время ЧЧ:ММ от 00:00 до 23:59. Конец «24:00» сервер принял бы, но поле времени его не даёт, поэтому и здесь его нет. */
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
const fromMin = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
/** Последнее время начала, после которого в дне ещё помещается интервал (до 23:55). */
const DAY_END = "23:55";

/**
 * Что не так с графиком (то же проверяет сервер, `CalendarService.setWorkingHours`): `order`: время не в формате ЧЧ:ММ
 * или «до» не позже «с»; `overlap`: интервалы одного дня пересекаются. null: график можно сохранять.
 */
export function hoursProblem(grid: WorkingHours[]): "order" | "overlap" | null {
  for (const h of grid) {
    const start = hh(h.start);
    const end = hh(h.end);
    if (!HHMM.test(start) || !HHMM.test(end) || start >= end) return "order";
  }
  for (const wd of WEEK) {
    const day = canonicalHours(grid.filter((h) => h.weekday === wd));
    for (let i = 1; i < day.length; i++) {
      const prev = day[i - 1];
      const cur = day[i];
      if (prev && cur && cur.start < prev.end) return "overlap";
    }
  }
  return null;
}

export const hoursValid = (grid: WorkingHours[]): boolean => hoursProblem(grid) === null;

/**
 * Новый интервал дня: с конца последнего интервала на час (но не позже 23:55). Если день уже кончается в 23:55
 * или позже, места нет: null, кнопка «добавить интервал» неактивна.
 */
export function nextInterval(dayRows: WorkingHours[]): { start: string; end: string } | null {
  const last = canonicalHours(dayRows).at(-1);
  const start = last ? hh(last.end) : "09:00";
  if (!HHMM.test(start) || start >= DAY_END) return null;
  return { start, end: fromMin(Math.min(toMin(start) + 60, toMin(DAY_END))) };
}

export const normalizeHours = (grid: WorkingHours[]): WorkingHours[] => grid.map((h) => ({ weekday: h.weekday, start: hh(h.start), end: hh(h.end) }));

/** Канонический вид для сравнения снимков: сервер отдаёт график отсортированным, сетка дописывает включённый день в конец. */
export const canonicalHours = (grid: WorkingHours[]): WorkingHours[] =>
  normalizeHours(grid).sort((a, b) => a.weekday - b.weekday || a.start.localeCompare(b.start) || a.end.localeCompare(b.end));

const snapshot = (grid: WorkingHours[]) => JSON.stringify(canonicalHours(grid));

/**
 * Одно и то же время на все включённые дни: у каждого включённого дня остаётся один интервал `start`–`end`,
 * выключенные дни не трогаем. Дальше любой день можно поправить отдельно.
 */
export function applyToWorkingDays(grid: WorkingHours[], start: string, end: string): WorkingHours[] {
  const days = WEEK.filter((wd) => grid.some((h) => h.weekday === wd));
  return days.map((weekday) => ({ weekday, start, end }));
}

/**
 * Часы работы мастера по дням: выключатель дня, один или несколько интервалов «с» и «до» (обед, разрыв),
 * сверху строка «на все рабочие дни». Один компонент в мастере настройки и на странице мастера.
 * На широком экране день в одну строку (название слева, интервалы справа), поля времени узкие.
 * Поля времени `required`: системный выбор времени в Chrome на Android тогда без кнопки «Удалить»,
 * и «Установить» помещается целиком.
 */
export function HoursGrid({ grid, onChange }: { grid: WorkingHours[]; onChange: (g: WorkingHours[]) => void }) {
  const { t, i18n } = useTranslation();
  const weekdayName = (wd: number) => new Intl.DateTimeFormat(i18n.language, { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 10, 1 + wd)));
  const rows = (wd: number) => grid.map((h, i) => ({ h, i })).filter((x) => x.h.weekday === wd);
  const setRow = (i: number, patch: Partial<WorkingHours>) => onChange(grid.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  const full = grid.length >= MAX_INTERVALS;
  const toggleDay = (wd: number, on: boolean) => {
    if (on && full) return;
    // Новый рабочий день получает время первого включённого дня (обычно оно у всех одно).
    const first = WEEK.flatMap((d) => grid.filter((h) => h.weekday === d))[0];
    onChange(on ? [...grid, { weekday: wd, start: hh(first?.start ?? "09:00"), end: hh(first?.end ?? "18:00") }] : grid.filter((h) => h.weekday !== wd));
  };
  const addInterval = (wd: number) => {
    const next = nextInterval(rows(wd).map((x) => x.h));
    if (next && !full) onChange([...grid, { weekday: wd, ...next }]);
  };
  const removeRow = (i: number) => onChange(grid.filter((_, j) => j !== i));

  return (
    <div>
      <ApplyAll grid={grid} onApply={(start, end) => onChange(applyToWorkingDays(grid, start, end))} />
      <div>
        {WEEK.map((wd) => {
          const dayRows = rows(wd);
          return (
            <div key={wd} className="border-b border-line py-2 last:border-b-0 lg:grid lg:grid-cols-[11rem_minmax(0,1fr)] lg:items-start lg:gap-4">
              <Toggle
                checked={dayRows.length > 0}
                disabled={dayRows.length === 0 && full}
                onChange={(v) => toggleDay(wd, v)}
                label={<span className="capitalize">{weekdayName(wd)}</span>}
              />
              <div className="space-y-1 lg:py-1">
                {dayRows.map(({ h, i }) => (
                  <div key={i} className="flex items-center gap-2">
                    <TimeInput value={hh(h.start)} onChange={(v) => setRow(i, { start: v })} label={`${weekdayName(wd)}: ${t("hours.from")}`} />
                    <span className="text-ink-3" aria-hidden="true">
                      –
                    </span>
                    <TimeInput value={hh(h.end)} onChange={(v) => setRow(i, { end: v })} label={`${weekdayName(wd)}: ${t("hours.to")}`} />
                    <IconButton size="sm" label={t("hours.removeInterval")} icon={<CloseIcon />} onClick={() => removeRow(i)} />
                  </div>
                ))}
                {dayRows.length > 0 && (
                  <button
                    type="button"
                    disabled={full || nextInterval(dayRows.map((x) => x.h)) === null}
                    className={`rounded text-sm text-ink-3 hover:text-ink disabled:opacity-40 ${focusRing}`}
                    onClick={() => addInterval(wd)}
                  >
                    + {t("hours.addInterval")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <Input
      type="time"
      required
      step={300}
      value={value}
      // Пустое значение (стёрли с клавиатуры) не принимаем: в графике всегда есть время.
      onChange={(e) => e.target.value && onChange(e.target.value)}
      // `!`: поле по умолчанию с отступами px-4 py-3, здесь компактнее (место под «09:00 AM» и значок часов).
      className="min-w-0 flex-1 px-3! py-2! lg:w-36 lg:flex-none"
      aria-label={label}
    />
  );
}

/** «С … до … на все рабочие дни»: по умолчанию время первого включённого дня. */
function ApplyAll({ grid, onApply }: { grid: WorkingHours[]; onApply: (start: string, end: string) => void }) {
  const { t } = useTranslation();
  const first = WEEK.flatMap((d) => grid.filter((h) => h.weekday === d))[0];
  const [start, setStart] = useState(hh(first?.start ?? "09:00"));
  const [end, setEnd] = useState(hh(first?.end ?? "18:00"));
  const valid = start < end;
  const any = grid.length > 0;
  return (
    <div className="mb-2 rounded-field bg-slate-50 p-3">
      <div className="mb-2 text-sm text-ink-2">{t("hours.applyAllHint")}</div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 lg:flex-none">
          <TimeInput value={start} onChange={setStart} label={t("hours.applyAllFrom")} />
          <span className="text-ink-3" aria-hidden="true">
            –
          </span>
          <TimeInput value={end} onChange={setEnd} label={t("hours.applyAllTo")} />
        </div>
        <Button variant="secondary" size="sm" className="w-full lg:w-auto" disabled={!valid || !any} onClick={() => onApply(start, end)}>
          {t("hours.applyAll")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Состояние автосохранения: `pending` ждёт паузы после правки, `saving` идёт запрос, `saved` сохранено;
 * `order` и `overlap` график неверный (см. `hoursProblem`), не сохраняется; `error` сбой сети, сервера, сессии или прав,
 * можно повторить; `rejected` сервер не принял сам график (`validation`), повтор того же не поможет.
 */
export type HoursSaveStatus = "idle" | "pending" | "saving" | "saved" | "error" | "rejected" | "order" | "overlap";

/** Есть правки, которые не сохранились и сами не сохранятся: уход со страницы спрашивает подтверждение. */
export const hoursUnsaved = (s: HoursSaveStatus): boolean => s === "error" || s === "rejected" || s === "order" || s === "overlap";
/** Есть правки не на сервере (в том числе ещё в пути): закрытие вкладки предупреждает браузер. */
export const hoursNotOnServer = (s: HoursSaveStatus): boolean => s === "pending" || s === "saving" || hoursUnsaved(s);

/**
 * Сервер не принял сам график (`validation`): тот же снимок повторять незачем. Остальное (сеть, 5xx, сессия, права)
 * показываем общей ошибкой с «Повторить».
 */
function refusedByServer(e: unknown): boolean {
  const code = trpcErrorCode(e);
  return code === "validation" || code === "BAD_REQUEST";
}

/**
 * Логика автосохранения без React (её проверяют unit-тесты): правка (`change`) ставит сохранение через `delay`,
 * `flush` сохраняет сразу. Запросы идут по одному: правка во время запроса уходит следующим запросом.
 * Неверный график не отправляется. После отказа сервера тот же снимок повторно не отправляется, только новая правка.
 */
export class HoursAutosaver {
  private latest: WorkingHours[] | null = null;
  private saved: string | null = null;
  private refused: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  /** Сохранения остановлены (`pause`): мастера удаляют, его график слать незачем. */
  private stopped = false;

  constructor(
    private readonly save: (hours: WorkingHours[]) => Promise<void>,
    private readonly onStatus: (s: HoursSaveStatus) => void,
    private readonly delay = AUTOSAVE_DELAY_MS,
  ) {}

  /** График с сервера: точка отсчёта. */
  init(grid: WorkingHours[]): void {
    this.latest = grid;
    this.saved = snapshot(grid);
  }

  get waiting(): boolean {
    return this.timer !== null;
  }

  change(grid: WorkingHours[]): void {
    this.latest = grid;
    this.clearTimer();
    const problem = hoursProblem(grid);
    if (problem) {
      this.onStatus(problem);
      return;
    }
    this.onStatus("pending");
    this.timer = setTimeout(() => void this.flush(), this.delay);
  }

  /** Сохранить сейчас (уход со страницы, сворачивание, «Повторить»). */
  async flush(): Promise<void> {
    this.clearTimer();
    if (this.running || this.stopped) return;
    this.running = true;
    try {
      for (;;) {
        const g = this.latest;
        if (!g) return;
        const problem = hoursProblem(g);
        if (problem) {
          this.onStatus(problem);
          return;
        }
        const snap = snapshot(g);
        if (snap === this.saved) {
          this.onStatus("saved");
          return;
        }
        if (snap === this.refused) {
          this.onStatus("rejected");
          return;
        }
        this.onStatus("saving");
        try {
          await this.save(normalizeHours(g));
          this.saved = snap;
          this.refused = null;
        } catch (e) {
          // Пока шёл запрос, график поправили: решает уже новая версия, а не судьба старой.
          const newer = this.latest !== g;
          if (refusedByServer(e)) {
            this.refused = snap;
            if (newer && !this.stopped) continue;
            this.onStatus("rejected");
            return;
          }
          const problem = this.latest ? hoursProblem(this.latest) : null;
          this.onStatus(problem ?? "error");
          return;
        }
      }
    } finally {
      this.running = false;
    }
  }

  /** Остановить сохранения (удаление мастера): ожидающее не уйдёт и при уходе со страницы. */
  pause(): void {
    this.stopped = true;
    this.clearTimer();
  }

  /** Снова сохранять (удаление не удалось): несохранённое уходит сразу. */
  resume(): void {
    this.stopped = false;
    void this.flush();
  }

  dispose(): void {
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}

/**
 * Автосохранение графика (решение 04.11.2026): без кнопки «Сохранить», через `AUTOSAVE_DELAY_MS` после последней правки,
 * сразу при уходе со страницы внутри кабинета (размонтирование) и при сворачивании приложения (`visibilitychange`).
 * Закрытие или перезагрузку вкладки с несохранённым браузер предупреждает (`notOnServer`): запрос из `pagehide`
 * браузер обычно обрывает. `unsaved`: правки, которые сами не сохранятся (ошибка, отказ, неверное время).
 */
export function useHoursAutosave(masterId: string, server: WorkingHours[] | undefined) {
  const [grid, setGridState] = useState<WorkingHours[] | null>(null);
  const [status, setStatus] = useState<HoursSaveStatus>("idle");
  const save = useMutation(
    trpc.calendar.workingHours.set.mutationOptions({
      onSuccess: (data) => queryClient.setQueryData(trpc.calendar.workingHours.get.queryKey({ masterId }), data),
    }),
  );
  const { mutateAsync } = save;
  const [saver] = useState(
    () =>
      new HoursAutosaver(async (hours) => {
        await mutateAsync({ masterId, hours });
      }, setStatus),
  );

  useEffect(() => {
    if (server && grid === null) {
      const g = normalizeHours(server);
      saver.init(g);
      setGridState(g);
    }
  }, [server, grid, saver]);

  const setGrid = useCallback(
    (g: WorkingHours[]) => {
      setGridState(g);
      saver.change(g);
    },
    [saver],
  );

  // Свернули приложение или ушли со страницы: несохранённое уходит сразу, без ожидания паузы.
  useEffect(() => {
    const now = () => {
      if (saver.waiting) void saver.flush();
    };
    const onVisibility = () => document.visibilityState === "hidden" && now();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", now);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", now);
      now();
      saver.dispose();
    };
  }, [saver]);

  return {
    grid,
    setGrid,
    status,
    retry: () => void saver.flush(),
    pause: () => saver.pause(),
    resume: () => saver.resume(),
    unsaved: hoursUnsaved(status),
    notOnServer: hoursNotOnServer(status),
  };
}

/** Строка состояния автосохранения под графиком. */
export function HoursSaveState({ status, onRetry }: { status: HoursSaveStatus; onRetry: () => void }) {
  const { t } = useTranslation();
  if (status === "order") return <Notice kind="error">{t("hours.badInterval")}</Notice>;
  if (status === "overlap") return <Notice kind="error">{t("hours.overlap")}</Notice>;
  if (status === "rejected") return <Notice kind="error">{t("hours.rejected")}</Notice>;
  if (status === "error") {
    return (
      <Notice kind="error">
        {t("hours.failed")}{" "}
        <button type="button" className={`rounded font-medium underline ${focusRing}`} onClick={onRetry}>
          {t("hours.retry")}
        </button>
      </Notice>
    );
  }
  return (
    <p className="min-h-5 text-sm text-ink-3" aria-live="polite">
      {status === "saving" || status === "pending" ? t("hours.saving") : status === "saved" ? `✓ ${t("hours.saved")}` : t("hours.autosave")}
    </p>
  );
}
